// The tile editor: pick characters from the official roster to build a script.

import { useMemo, useState } from "react";
import { BASE_CHARACTERS, TEAM_ORDER } from "@cc/shared";
import type { Team } from "@cc/shared";
import { teamLabel } from "../../../lib/ui.js";
import { Button, Input, Label } from "../../ui/primitives.js";
import { LayoutToggle, RoleTile, TeamHeading, TeamSummary, TileGroup } from "./tiles.js";

export function Editor({
  initial,
  busy,
  onSave,
  onUse,
}: {
  initial: { name: string; ids: string[] };
  busy: boolean;
  onSave: (name: string, ids: string[]) => void;
  onUse: (name: string, ids: string[]) => void;
}) {
  const [name, setName] = useState(initial.name);
  const [selected, setSelected] = useState(() => new Set(initial.ids));
  const [query, setQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState<Team | "all">("all");

  const known = useMemo(() => new Set(BASE_CHARACTERS.map((c) => c.id)), []);
  const unknown = initial.ids.filter((id) => !known.has(id) && selected.has(id));

  const visible = BASE_CHARACTERS.filter(
    (c) => (teamFilter === "all" || c.team === teamFilter) && c.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  // Emitted grouped by team, then in the base roster's order — the order a script reads best in.
  const orderedIds = (): string[] => [
    ...TEAM_ORDER.flatMap((t) => BASE_CHARACTERS.filter((c) => c.team === t && selected.has(c.id)).map((c) => c.id)),
    ...unknown,
  ];

  const toggle = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const setMany = (ids: string[], on: boolean): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) (on ? next.add(id) : next.delete(id));
      return next;
    });

  const teams = [...selected].map((id) => BASE_CHARACTERS.find((c) => c.id === id)?.team ?? null);
  const canSave = name.trim().length > 0 && selected.size > 0 && !busy;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div>
        <Label htmlFor="cc-script-name">Script name</Label>
        <Input
          id="cc-script-name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Friday Night Chaos"
          className="mt-1"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Input
          aria-label="Search characters"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className="w-40 px-2 py-1"
        />
        {(["all", ...TEAM_ORDER] as const).map((t) => (
          <Button
            key={t}
            size="sm"
            variant={teamFilter === t ? "primary" : "secondary"}
            onClick={() => setTeamFilter(t)}
          >
            {t === "all" ? "All" : teamLabel(t)}
          </Button>
        ))}
        <LayoutToggle className="ml-auto" />
      </div>

      <div className="cc-scroll cc-scroll-strong min-h-0 flex-1 overflow-y-auto pr-1">
        {TEAM_ORDER.filter((t) => visible.some((c) => c.team === t)).map((team) => {
          const chars = visible.filter((c) => c.team === team);
          const ids = chars.map((c) => c.id);
          const count = ids.filter((id) => selected.has(id)).length;
          return (
            <div key={team} className="mb-2">
              <TeamHeading team={team}>
                <span className="font-normal normal-case text-parchment-dim">
                  {count}/{chars.length}
                </span>
                {(
                  [
                    ["Select All", true, count === ids.length],
                    ["Deselect All", false, count === 0],
                  ] as const
                ).map(([label, on, noop]) => (
                  <button
                    key={label}
                    type="button"
                    disabled={noop}
                    className="font-normal normal-case text-parchment-dim underline hover:text-parchment disabled:no-underline disabled:opacity-40"
                    onClick={() => setMany(ids, on)}
                  >
                    {label}
                  </button>
                ))}
              </TeamHeading>
              <TileGroup>
                {chars.map((c) => (
                  <RoleTile key={c.id} character={c} selected={selected.has(c.id)} onClick={() => toggle(c.id)} />
                ))}
              </TileGroup>
            </div>
          );
        })}
        {visible.length === 0 && <p className="text-sm text-parchment-dim">Nothing matches that.</p>}
      </div>

      {unknown.length > 0 && (
        <p className="text-xs text-parchment-dim">
          Not in the official list, kept as-is: {unknown.join(", ")}.{" "}
          <button
            type="button"
            className="underline hover:text-parchment"
            onClick={() => setMany(unknown, false)}
          >
            Remove them
          </button>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-ink-line pt-3">
        <TeamSummary teams={teams} />
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="ghost" disabled={!canSave} onClick={() => onUse(name.trim(), orderedIds())}>
            Use without saving
          </Button>
          <Button variant="primary" disabled={!canSave} onClick={() => onSave(name.trim(), orderedIds())}>
            Save & use
          </Button>
        </div>
      </div>
    </div>
  );
}
